// Хуки TanStack Query поверх engine API. Ключи стабильные, чтобы SSE мог их инвалидировать.
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { engineApi, notifyApi, unwrap, type Schemas } from "./client";

export type Sport = Schemas["SportOut"];
export type League = Schemas["LeagueOut"];
export type Season = Schemas["SeasonOut"];
export type Gameweek = Schemas["GameweekOut"];
export type Club = Schemas["ClubOut"];
export type Player = Schemas["PlayerOut"];
export type Competition = Schemas["CompetitionOut"];
export type Entry = Schemas["EntryOut"];
export type Lineup = Schemas["LineupOut"];
export type LineupPlayer = Schemas["LineupPlayerOut"];
export type LineupPoints = Schemas["LineupPointsOut"];
export type Leaderboard = Schemas["LeaderboardOut"];
export type LineupIn = Schemas["LineupIn"];

export const keys = {
  sports: ["sports"] as const,
  leagues: (sport?: string) => ["leagues", sport ?? "all"] as const,
  seasons: (leagueId?: string, status?: string) =>
    ["seasons", leagueId ?? "all", status ?? "all"] as const,
  season: (id: string) => ["season", id] as const,
  gameweeks: (seasonId: string) => ["gameweeks", seasonId] as const,
  clubs: (seasonId: string) => ["clubs", seasonId] as const,
  players: (seasonId: string, f: Record<string, string | undefined>) =>
    ["players", seasonId, f] as const,
  competitions: (seasonId?: string) => ["competitions", seasonId ?? "all"] as const,
  competition: (id: string) => ["competition", id] as const,
  leaderboard: (id: string, offset: number) => ["leaderboard", id, offset] as const,
  myEntries: (status?: string) => ["me", "entries", status ?? "all"] as const,
  currentLineup: (entryId: string) => ["lineup", entryId, "current"] as const,
  lineup: (entryId: string, gwId: string) => ["lineup", entryId, gwId] as const,
  points: (entryId: string, gwId: string) => ["points", entryId, gwId] as const,
};

const day = 24 * 3600 * 1000;

export const useSports = () =>
  useQuery({
    queryKey: keys.sports,
    queryFn: async () => unwrap(await engineApi.GET("/api/v1/sports")),
    staleTime: day,
  });

export const useLeagues = (sport?: string) =>
  useQuery({
    queryKey: keys.leagues(sport),
    queryFn: async () =>
      unwrap(await engineApi.GET("/api/v1/leagues", { params: { query: { sport } } })),
    staleTime: day,
  });

export const useSeasons = (leagueId?: string, status?: string) =>
  useQuery({
    queryKey: keys.seasons(leagueId, status),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/seasons", {
          params: { query: { league_id: leagueId, status } },
        }),
      ),
    staleTime: 3600 * 1000,
  });

export const useSeason = (id: string | undefined) =>
  useQuery({
    queryKey: keys.season(id ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/seasons/{season_id}", {
          params: { path: { season_id: id! } },
        }),
      ),
    enabled: !!id,
    staleTime: day,
  });

export const useGameweeks = (seasonId: string | undefined) =>
  useQuery({
    queryKey: keys.gameweeks(seasonId ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/seasons/{season_id}/gameweeks", {
          params: { path: { season_id: seasonId! } },
        }),
      ),
    enabled: !!seasonId,
    staleTime: 3600 * 1000,
  });

export const useClubs = (seasonId: string | undefined) =>
  useQuery({
    queryKey: keys.clubs(seasonId ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/seasons/{season_id}/clubs", {
          params: { path: { season_id: seasonId! } },
        }),
      ),
    enabled: !!seasonId,
    staleTime: day,
  });

export interface PlayerFilters {
  position?: string;
  club_id?: string;
  search?: string;
}

/** Все игроки сезона постранично по курсору; для конструктора грузим всё сразу (сотни строк). */
export const usePlayers = (seasonId: string | undefined, filters: PlayerFilters = {}) =>
  useInfiniteQuery({
    queryKey: keys.players(seasonId ?? "", filters as Record<string, string | undefined>),
    queryFn: async ({ pageParam }) =>
      unwrap(
        await engineApi.GET("/api/v1/seasons/{season_id}/players", {
          params: {
            path: { season_id: seasonId! },
            query: { ...filters, cursor: pageParam, limit: 200 },
          },
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!seasonId,
    staleTime: 3600 * 1000,
  });

export const useCompetitions = (seasonId?: string) =>
  useQuery({
    queryKey: keys.competitions(seasonId),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/competitions", {
          params: { query: { season_id: seasonId, limit: 100 } },
        }),
      ),
    enabled: seasonId !== "",
  });

export const useCompetition = (id: string | undefined) =>
  useQuery({
    queryKey: keys.competition(id ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/competitions/{competition_id}", {
          params: { path: { competition_id: id! } },
        }),
      ),
    enabled: !!id,
  });

export const useLeaderboard = (id: string | undefined, offset = 0, limit = 50) =>
  useQuery({
    queryKey: keys.leaderboard(id ?? "", offset),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/competitions/{competition_id}/leaderboard", {
          params: { path: { competition_id: id! }, query: { offset, limit } },
        }),
      ),
    enabled: !!id,
  });

export const useMyEntries = (status?: string) =>
  useQuery({
    queryKey: keys.myEntries(status),
    queryFn: async () =>
      unwrap(await engineApi.GET("/api/v1/me/entries", { params: { query: { status } } })),
  });

export const useCurrentLineup = (entryId: string | undefined) =>
  useQuery({
    queryKey: keys.currentLineup(entryId ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/entries/{entry_id}/lineups/current", {
          params: { path: { entry_id: entryId! } },
        }),
      ),
    enabled: !!entryId,
  });

export const useLineup = (entryId: string | undefined, gwId: string | undefined) =>
  useQuery({
    queryKey: keys.lineup(entryId ?? "", gwId ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/entries/{entry_id}/lineups/{gameweek_id}", {
          params: { path: { entry_id: entryId!, gameweek_id: gwId! } },
        }),
      ),
    enabled: !!entryId && !!gwId,
    retry: false,
  });

export const useLineupPoints = (entryId: string | undefined, gwId: string | undefined) =>
  useQuery({
    queryKey: keys.points(entryId ?? "", gwId ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/entries/{entry_id}/lineups/{gameweek_id}/points", {
          params: { path: { entry_id: entryId!, gameweek_id: gwId! } },
        }),
      ),
    enabled: !!entryId && !!gwId,
  });

export function usePutLineup(entryId: string, gwId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: LineupIn) =>
      unwrap(
        await engineApi.PUT("/api/v1/entries/{entry_id}/lineups/{gameweek_id}", {
          params: { path: { entry_id: entryId, gameweek_id: gwId } },
          body,
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(keys.lineup(entryId, gwId), data);
      qc.invalidateQueries({ queryKey: keys.currentLineup(entryId) });
      qc.invalidateQueries({ queryKey: ["me", "entries"] });
    },
  });
}

export function useCreateCompetition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schemas["CompetitionCreateIn"]) =>
      unwrap(await engineApi.POST("/api/v1/competitions", { body })),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: keys.competitions(c.season_id) });
      qc.invalidateQueries({ queryKey: ["me", "entries"] });
      qc.invalidateQueries({ queryKey: ["me", "competitions"] });
    },
  });
}

export function useJoinCompetition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { competitionId: string; team_name?: string; join_code?: string }) =>
      unwrap(
        await engineApi.POST("/api/v1/competitions/{competition_id}/join", {
          params: { path: { competition_id: args.competitionId } },
          body: { team_name: args.team_name, join_code: args.join_code },
        }),
      ),
    onSuccess: (entry) => {
      qc.invalidateQueries({ queryKey: keys.competition(entry.competition_id) });
      qc.invalidateQueries({ queryKey: ["competitions"] });
      qc.invalidateQueries({ queryKey: ["me", "entries"] });
      qc.invalidateQueries({ queryKey: ["me", "competitions"] });
    },
  });
}

/** Открытые незавершённые соревнования и все мои (с командой, владелец, соадмин) */
export const useCompetitionFeed = () =>
  useQuery({
    queryKey: ["me", "competitions", "feed"] as const,
    queryFn: async () => unwrap(await engineApi.GET("/api/v1/me/competitions/feed")),
  });

export async function fetchCompetitionByCode(code: string) {
  return unwrap(
    await engineApi.GET("/api/v1/competitions/by-code/{join_code}", {
      params: { path: { join_code: code } },
    }),
  );
}

// ---------------------------------------------------------------- команда: туры, имя, удаление

export type EntryGameweek = Schemas["EntryGameweekOut"];
export type MatchPoints = Schemas["MatchPointsOut"];
export type Member = Schemas["MemberOut"];
export type RuleSet = Schemas["RuleSetOut"];
export type Notification = Schemas["NotificationOut"];
export type PlayerCard = Schemas["PlayerCardOut"];
export type Match = Schemas["MatchOut"];

export const moreKeys = {
  entryGameweeks: (entryId: string) => ["entry", entryId, "gameweeks"] as const,
  matchPoints: (entryId: string, gwId: string) => ["entry", entryId, "match-points", gwId] as const,
  myCompetitions: ["me", "competitions"] as const,
  members: (id: string) => ["competition", id, "members"] as const,
  playersPage: (seasonId: string, f: Record<string, string | number | undefined>) =>
    ["players-page", seasonId, f] as const,
  playerCard: (seasonId: string, playerId: string) => ["player", seasonId, playerId] as const,
  matches: (seasonId: string, gwId?: string) => ["matches", seasonId, gwId ?? "all"] as const,
  rules: ["rules"] as const,
  notifications: ["me", "notifications"] as const,
  unread: ["me", "notifications", "unread"] as const,
  prefs: ["me", "notification-prefs"] as const,
  leaderboardAround: (id: string) => ["leaderboard", id, "around"] as const,
};

/** Туры команды: только с составом, новые первыми */
export const useEntryGameweeks = (entryId: string | undefined) =>
  useQuery({
    queryKey: moreKeys.entryGameweeks(entryId ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/entries/{entry_id}/gameweeks", {
          params: { path: { entry_id: entryId! } },
        }),
      ),
    enabled: !!entryId,
  });

/** Очки игроков состава за каждый матч тура (экран «Матчи») */
export const useMatchPoints = (entryId: string | undefined, gwId: string | undefined) =>
  useQuery({
    queryKey: moreKeys.matchPoints(entryId ?? "", gwId ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/entries/{entry_id}/gameweeks/{gameweek_id}/match-points", {
          params: { path: { entry_id: entryId!, gameweek_id: gwId! } },
        }),
      ),
    enabled: !!entryId && !!gwId,
  });

export function useRenameEntry(entryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (team_name: string) =>
      unwrap(
        await engineApi.PATCH("/api/v1/entries/{entry_id}", {
          params: { path: { entry_id: entryId } },
          body: { team_name },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "entries"] });
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
    },
  });
}

export function useDeleteEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string) =>
      unwrap(
        await engineApi.DELETE("/api/v1/entries/{entry_id}", {
          params: { path: { entry_id: entryId } },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me", "entries"] });
      qc.invalidateQueries({ queryKey: ["competition"] });
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
    },
  });
}

/** Таблица с соседями по месту: топ limit плюс around соседей выше и ниже меня */
export const useLeaderboardAround = (id: string | undefined, around = 1, limit = 3) =>
  useQuery({
    queryKey: moreKeys.leaderboardAround(id ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/competitions/{competition_id}/leaderboard", {
          params: { path: { competition_id: id! }, query: { offset: 0, limit, around } },
        }),
      ),
    enabled: !!id,
  });

// ---------------------------------------------------------------- управление лигой

export const useMyCompetitions = () =>
  useQuery({
    queryKey: moreKeys.myCompetitions,
    queryFn: async () => unwrap(await engineApi.GET("/api/v1/me/competitions")),
  });

export const useMembers = (id: string | undefined) =>
  useQuery({
    queryKey: moreKeys.members(id ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/competitions/{competition_id}/members", {
          params: { path: { competition_id: id! } },
        }),
      ),
    enabled: !!id,
  });

function useCompetitionMutation<TArgs>(id: string, fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.competition(id) });
      qc.invalidateQueries({ queryKey: moreKeys.members(id) });
      qc.invalidateQueries({ queryKey: moreKeys.myCompetitions });
      qc.invalidateQueries({ queryKey: ["leaderboard", id] });
      qc.invalidateQueries({ queryKey: ["me", "entries"] });
    },
  });
}

export const useUpdateCompetition = (id: string) =>
  useCompetitionMutation(id, async (body: Schemas["CompetitionUpdateIn"]) =>
    unwrap(
      await engineApi.PATCH("/api/v1/competitions/{competition_id}", {
        params: { path: { competition_id: id } },
        body,
      }),
    ),
  );

export const useReissueCode = (id: string) =>
  useCompetitionMutation(id, async () =>
    unwrap(
      await engineApi.POST("/api/v1/competitions/{competition_id}/join-code", {
        params: { path: { competition_id: id } },
      }),
    ),
  );

export const useKickMember = (id: string) =>
  useCompetitionMutation(id, async (entryId: string) =>
    unwrap(
      await engineApi.DELETE("/api/v1/competitions/{competition_id}/members/{entry_id}", {
        params: { path: { competition_id: id, entry_id: entryId } },
      }),
    ),
  );

export const useSetAdmin = (id: string) =>
  useCompetitionMutation(id, async (args: { userId: string; on: boolean }) =>
    args.on
      ? unwrap(
          await engineApi.PUT("/api/v1/competitions/{competition_id}/admins/{user_id}", {
            params: { path: { competition_id: id, user_id: args.userId } },
          }),
        )
      : unwrap(
          await engineApi.DELETE("/api/v1/competitions/{competition_id}/admins/{user_id}", {
            params: { path: { competition_id: id, user_id: args.userId } },
          }),
        ),
  );

export const useTransferCompetition = (id: string) =>
  useCompetitionMutation(id, async (userId: string) =>
    unwrap(
      await engineApi.POST("/api/v1/competitions/{competition_id}/transfer", {
        params: { path: { competition_id: id } },
        body: { user_id: userId },
      }),
    ),
  );

export function useDeleteCompetition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await engineApi.DELETE("/api/v1/competitions/{competition_id}", {
          params: { path: { competition_id: id } },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: moreKeys.myCompetitions });
      qc.invalidateQueries({ queryKey: ["me", "entries"] });
      qc.invalidateQueries({ queryKey: ["competitions"] });
    },
  });
}

// ---------------------------------------------------------------- игроки, матчи, правила

export interface PlayersPageFilters {
  position?: string;
  club_id?: string;
  search?: string;
  price_min?: number;
  price_max?: number;
  sort?: "name" | "alpha" | "points" | "form" | "price" | "ppm" | "picked";
  order?: "asc" | "desc";
  offset?: number;
  limit?: number;
}

/** Страница раздела «Игроки»: сортировка и смещение, total в ответе */
export const usePlayersPage = (seasonId: string | undefined, f: PlayersPageFilters) =>
  useQuery({
    queryKey: moreKeys.playersPage(
      seasonId ?? "",
      f as Record<string, string | number | undefined>,
    ),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/seasons/{season_id}/players", {
          params: { path: { season_id: seasonId! }, query: { ...f, limit: f.limit ?? 25 } },
        }),
      ),
    enabled: !!seasonId,
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });

export const usePlayerCard = (seasonId: string | undefined, playerId: string | undefined) =>
  useQuery({
    queryKey: moreKeys.playerCard(seasonId ?? "", playerId ?? ""),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/seasons/{season_id}/players/{player_id}", {
          params: { path: { season_id: seasonId!, player_id: playerId! } },
        }),
      ),
    enabled: !!seasonId && !!playerId,
    staleTime: 60 * 1000,
  });

export const useMatches = (seasonId: string | undefined, gwId?: string) =>
  useQuery({
    queryKey: moreKeys.matches(seasonId ?? "", gwId),
    queryFn: async () =>
      unwrap(
        await engineApi.GET("/api/v1/seasons/{season_id}/matches", {
          params: { path: { season_id: seasonId! }, query: { gameweek_id: gwId } },
        }),
      ),
    enabled: !!seasonId,
    staleTime: 60 * 1000,
  });

export const useRules = () =>
  useQuery({
    queryKey: moreKeys.rules,
    queryFn: async () => unwrap(await engineApi.GET("/api/v1/rules")),
    staleTime: day,
  });

// ---------------------------------------------------------------- уведомления

export const useNotifications = (limit = 30) =>
  useInfiniteQuery({
    queryKey: moreKeys.notifications,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await engineApi.GET("/api/v1/me/notifications", {
          params: { query: { before: pageParam, limit } },
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_before ?? undefined,
  });

export const useUnreadCount = () =>
  useQuery({
    queryKey: moreKeys.unread,
    queryFn: async () => unwrap(await engineApi.GET("/api/v1/me/notifications/unread")),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[] | null) =>
      unwrap(await engineApi.POST("/api/v1/me/notifications/read", { body: { ids } })),
    onSuccess: (data) => {
      qc.setQueryData(moreKeys.unread, data);
      qc.invalidateQueries({ queryKey: moreKeys.notifications });
    },
  });
}

export const useNotificationPrefs = () =>
  useQuery({
    queryKey: moreKeys.prefs,
    queryFn: async () => unwrap(await engineApi.GET("/api/v1/me/notification-prefs")),
    staleTime: day,
  });

export function useSetNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (prefs: Record<string, boolean>) =>
      unwrap(await engineApi.PUT("/api/v1/me/notification-prefs", { body: { prefs } })),
    onSuccess: (data) => qc.setQueryData(moreKeys.prefs, data),
  });
}

// ---------------------------------------------------------------- Telegram (сервис notifications)

export type TelegramStatus =
  import("./notifications.gen").components["schemas"]["TelegramStatusOut"];
const telegramKey = ["telegram", "status"] as const;

/** Статус Telegram; пока ждём подтверждения в боте, опрашиваем каждые 3 секунды */
export const useTelegramStatus = (waiting = false) =>
  useQuery({
    queryKey: telegramKey,
    queryFn: async () => unwrap(await notifyApi.GET("/api/v1/telegram")),
    refetchInterval: waiting ? 3000 : false,
  });

export function useTelegramLink() {
  return useMutation({
    mutationFn: async () => unwrap(await notifyApi.POST("/api/v1/telegram/link")),
  });
}

export function useTelegramTest() {
  return useMutation({
    mutationFn: async () => unwrap(await notifyApi.POST("/api/v1/telegram/test")),
  });
}

export function useTelegramDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap(await notifyApi.DELETE("/api/v1/telegram")),
    onSuccess: (data) => qc.setQueryData(telegramKey, data),
  });
}

/** Подсказка свободного названия (кнопка «Сгенерировать»), app/names.py в engine */
export type NameTarget =
  { kind: "team"; competitionId?: string } | { kind: "competition"; seasonId: string };

export async function suggestName(target: NameTarget): Promise<string> {
  const out =
    target.kind === "team"
      ? unwrap(
          await engineApi.GET("/api/v1/names/team", {
            params: { query: { competition_id: target.competitionId } },
          }),
        )
      : unwrap(
          await engineApi.GET("/api/v1/names/competition", {
            params: { query: { season_id: target.seasonId } },
          }),
        );
  return out.name;
}

/** Все мои команды одной лиги в туре (экран «Матчи»): состав тура, очки игроков за каждый
 *  матч и строка тура команды. Команд в лиге бывает несколько. Нет состава на тур: для
 *  открытого тура берём текущий (он скопируется на дедлайне), для начавшегося никого. */
export interface SeasonSquad {
  entry: Entry;
  players: LineupPlayer[];
  matchPoints: MatchPoints[];
  gameweek: EntryGameweek | undefined;
}

export function useSeasonSquads(
  entries: Entry[],
  gwId: string | undefined,
  gwOpen: boolean,
): SeasonSquad[] {
  const lineups = useQueries({
    queries: entries.map((e) => ({
      queryKey: keys.lineup(e.id, gwId ?? ""),
      queryFn: async () =>
        unwrap(
          await engineApi.GET("/api/v1/entries/{entry_id}/lineups/{gameweek_id}", {
            params: { path: { entry_id: e.id, gameweek_id: gwId! } },
          }),
        ),
      enabled: !!gwId,
      retry: false,
    })),
  });
  // нет состава на этот тур (тур ещё не собран): берём текущий состав команды
  const current = useQueries({
    queries: entries.map((e, i) => ({
      queryKey: keys.currentLineup(e.id),
      queryFn: async () =>
        unwrap(
          await engineApi.GET("/api/v1/entries/{entry_id}/lineups/current", {
            params: { path: { entry_id: e.id } },
          }),
        ),
      enabled: gwOpen && !!lineups[i]?.isError,
    })),
  });
  const points = useQueries({
    queries: entries.map((e) => ({
      queryKey: moreKeys.matchPoints(e.id, gwId ?? ""),
      queryFn: async () =>
        unwrap(
          await engineApi.GET("/api/v1/entries/{entry_id}/gameweeks/{gameweek_id}/match-points", {
            params: { path: { entry_id: e.id, gameweek_id: gwId! } },
          }),
        ),
      enabled: !!gwId,
    })),
  });
  const gws = useQueries({
    queries: entries.map((e) => ({
      queryKey: moreKeys.entryGameweeks(e.id),
      queryFn: async () =>
        unwrap(
          await engineApi.GET("/api/v1/entries/{entry_id}/gameweeks", {
            params: { path: { entry_id: e.id } },
          }),
        ),
    })),
  });
  return entries.map((entry, i) => ({
    entry,
    players: lineups[i]?.data?.players ?? (gwOpen ? current[i]?.data?.players : undefined) ?? [],
    matchPoints: points[i]?.data ?? [],
    gameweek: gws[i]?.data?.find((g) => g.gameweek_id === gwId),
  }));
}
